/**
 * «Πιάτα ημέρας» στους καταλόγους του ταμείου και του PDA (backend services/dailyDishes, migration 278).
 *
 * Με ανοιχτή τη λειτουργία και ορισμένη κατηγορία:
 *  - η κατηγορία πιάτων ημέρας μπαίνει ΠΡΩΤΗ στις κατηγορίες (στο Take Away το κουτί «Αγαπημένα», όταν
 *    υπάρχουν αγαπημένα, μένει πριν από αυτήν — ProductCatalog/ProductCatalogV2)·
 *  - δείχνει ΜΟΝΟ τα σημερινά· τα υπόλοιπα της βιβλιοθήκης δεν φαίνονται πουθενά στον κατάλογο (ούτε στην
 *    αναζήτηση)· χωρίς κανένα σημερινό η κατηγορία δεν φαίνεται καθόλου.
 * Κλειστή λειτουργία (ή χωρίς κατηγορία, ή παλιός server) → ΑΚΡΙΒΩΣ οι ίδιοι πίνακες που μπήκαν.
 *
 * ΜΟΝΟ για ό,τι ΒΛΕΠΕΙ ο ταμίας/σερβιτόρος: οι οθόνες κρατούν την πλήρη λίστα προϊόντων για τις άλλες
 * δουλειές τους (γραμμές που ήδη υπάρχουν σε τραπέζι, επανάληψη, διόρθωση) — ένα πιάτο που βγήκε από τα
 * σημερινά ενώ είναι ήδη σε λογαριασμό δεν πρέπει να «χαθεί» από εκεί.
 */
import type { Category, Product } from '../types/order';

export interface DailyDishesToday {
  enabled: boolean;
  categoryId: number | null;
  productIds: number[];
}

export const DAILY_DISHES_OFF: DailyDishesToday = Object.freeze({ enabled: false, categoryId: null, productIds: [] }) as DailyDishesToday;

/** Το socket του καταστήματος (backend SOCKET_EVENT) και το ίδιο γεγονός μέσα στην καρτέλα. */
export const DAILY_DISHES_SOCKET_EVENT = 'daily-dishes:changed';
export const DAILY_DISHES_WINDOW_EVENT = 'xf:daily-dishes-changed';

/** Ειδοποίηση των καταλόγων της ίδιας καρτέλας (ο ταμίας μόλις άλλαξε τα σημερινά από το μενού). */
export function announceDailyDishesChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent(DAILY_DISHES_WINDOW_EVENT));
  } catch { /* παλιός browser: θα το φέρει το socket */ }
}

/** Ενεργή λειτουργία με κατηγορία — μόνο τότε αλλάζει κάτι στον κατάλογο. */
export const dailyDishesActive = (d: DailyDishesToday | null | undefined): d is DailyDishesToday & { categoryId: number } =>
  !!d && d.enabled === true && typeof d.categoryId === 'number';

/** Ποια προϊόντα ΔΕΝ φαίνονται: της κατηγορίας, όχι σημερινά (τα τέλη πάντα φαίνονται). */
export function hiddenDailyProductIds(products: Product[], d: DailyDishesToday | null | undefined): Set<number> {
  const hidden = new Set<number>();
  if (!dailyDishesActive(d)) return hidden;
  const today = new Set(d.productIds);
  for (const p of products) {
    if (p.category_id === d.categoryId && p.fee_category == null && !today.has(p.id)) hidden.add(p.id);
  }
  return hidden;
}

/** Υπάρχει έστω ένα σημερινό στον κατάλογο που φορτώθηκε; */
const hasTodayIn = (products: Product[], d: DailyDishesToday & { categoryId: number }) => {
  const today = new Set(d.productIds);
  return products.some(p => p.category_id === d.categoryId && today.has(p.id));
};

/**
 * Η κατηγορία πιάτων ημέρας πρώτη (αν είναι υποκατηγορία: πρώτη μέσα στη γονική της, και η γονική πρώτη)·
 * χωρίς κανένα σημερινό φεύγει (και η γονική μένει όπου ήταν). Νέοι πίνακες, τα αρχικά δεν αλλάζουν.
 */
export function orderDailyFirst(categories: Category[], categoryId: number, showDaily: boolean): Category[] {
  const top = categories.findIndex(c => c.id === categoryId);
  if (top >= 0) {
    const cat = categories[top];
    const rest = categories.filter((_, i) => i !== top);
    // Χωρίς σημερινά φεύγει — εκτός αν έχει υποκατηγορίες (τα προϊόντα τους δεν είναι πιάτα ημέρας): τότε
    // μένει όπου ήταν.
    if (!showDaily) return cat.subcategories && cat.subcategories.length > 0 ? categories : rest;
    return [cat, ...rest];
  }
  const parentIdx = categories.findIndex(c => (c.subcategories || []).some(s => s.id === categoryId));
  if (parentIdx < 0) return categories;
  const parent = categories[parentIdx];
  const subs = parent.subcategories || [];
  const daily = subs.find(s => s.id === categoryId) as Category;
  const others = subs.filter(s => s.id !== categoryId);
  if (!showDaily) return categories.map((c, i) => (i === parentIdx ? { ...c, subcategories: others } : c));
  const moved = { ...parent, subcategories: [daily, ...others] };
  return [moved, ...categories.filter((_, i) => i !== parentIdx)];
}

export interface DailyCatalog {
  categories: Category[];
  products: Product[];
  /** Τα πιάτα της βιβλιοθήκης που δεν είναι σήμερα (για οθόνες που φιλτράρουν μόνες τους, π.χ. PDA). */
  hiddenProductIds: Set<number>;
}

/** Ό,τι δείχνει ο κατάλογος. Κλειστό → οι ίδιοι πίνακες (ίδια αναφορά), κανένα κρυφό. */
export function applyDailyDishes(categories: Category[], products: Product[], d: DailyDishesToday | null | undefined): DailyCatalog {
  if (!dailyDishesActive(d)) return { categories, products, hiddenProductIds: new Set() };
  const hiddenProductIds = hiddenDailyProductIds(products, d);
  return {
    categories: orderDailyFirst(categories, d.categoryId, hasTodayIn(products, d)),
    products: hiddenProductIds.size === 0 ? products : products.filter(p => !hiddenProductIds.has(p.id)),
    hiddenProductIds,
  };
}
